import React, { useState, useEffect, useMemo } from 'react';
import { 
  Cloud, 
  Building2, 
  MapPin, 
  Server, 
  Users, 
  Search, 
  ChevronRight, 
  ChevronDown,
  Circle,
  Info
} from 'lucide-react';

// Types
interface Reflector {
  type: 'reflector';
  id: string;
  display_name: string;
  status: 'online' | 'offline' | 'degraded';
  role?: string;
  version?: string;
  vpc?: string;
  tag?: string;
  last_sync?: string;
  creation_date?: string;
  license_type?: string;
  license_serial?: string;
  iam_permissions?: string;
}

interface Cluster {
  type: 'cluster';
  id: string;
  display_name: string;
  status: 'online' | 'offline' | 'degraded';
  health: 'Healthy' | 'Degraded' | 'Offline';
  reflectors: { id: string; name: string }[];
}

interface Region {
  name: string;
  reflectors: Reflector[];
  clusters: Cluster[];
}

interface Account {
  id: string;
  display_name: string;
  regions: Region[];
}

interface CloudData {
  name: string;
  accounts: Account[];
}

interface CloudInventory {
  clouds: CloudData[];
}

interface SelectedEntity {
  cloud: string;
  account_id: string;
  account_display_name: string;
  region: string;
  type: 'reflector' | 'cluster';
  id: string;
  display_name: string;
}

interface CloudInventoryProps {
  onEntitySelect: (entity: SelectedEntity | null) => void;
}

const CloudInventoryPanel: React.FC<CloudInventoryProps> = ({ onEntitySelect }) => {
  const [inventory, setInventory] = useState<CloudInventory | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  // Load inventory data
  useEffect(() => {
    const loadInventory = async () => {
      try {
        const response = await fetch('/data/cloud_inventory.json');
        const data = await response.json();
        setInventory(data);
      } catch (error) {
        console.error('Failed to load cloud inventory:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInventory();
  }, []);

  // Filter data based on search term
  const filteredInventory = useMemo(() => {
    if (!inventory || !searchTerm) return inventory;

    const filterRecursive = (clouds: CloudData[]): CloudData[] => {
      return clouds.map(cloud => ({
        ...cloud,
        accounts: cloud.accounts.map(account => ({
          ...account,
          regions: account.regions.map(region => ({
            ...region,
            reflectors: region.reflectors.filter(reflector =>
              reflector.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              reflector.id.toLowerCase().includes(searchTerm.toLowerCase())
            ),
            clusters: region.clusters.filter(cluster =>
              cluster.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              cluster.id.toLowerCase().includes(searchTerm.toLowerCase())
            )
          })).filter(region => 
            region.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            region.reflectors.length > 0 ||
            region.clusters.length > 0
          )
        })).filter(account =>
          account.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          account.id.includes(searchTerm) ||
          account.regions.length > 0
        )
      })).filter(cloud =>
        cloud.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cloud.accounts.length > 0
      );
    };

    return { clouds: filterRecursive(inventory.clouds) };
  }, [inventory, searchTerm]);

  // Handle entity selection
  const handleEntitySelect = (entity: SelectedEntity) => {
    setSelectedEntity(entity);
    onEntitySelect(entity);
  };


  // Toggle account expansion
  const toggleAccount = (accountId: string) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedAccounts(newExpanded);
  };

  // Toggle region expansion
  const toggleRegion = (regionKey: string) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(regionKey)) {
      newExpanded.delete(regionKey);
    } else {
      newExpanded.add(regionKey);
    }
    setExpandedRegions(newExpanded);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-500';
      case 'offline': return 'text-red-500';
      case 'degraded': return 'text-yellow-500';
      default: return 'text-gray-500';
    }
  };

  // Get health color

  if (loading) {
    return (
      <div className="w-full bg-white flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading inventory...</p>
        </div>
      </div>
    );
  }

  if (!filteredInventory) {
    return (
      <div className="w-full bg-white flex items-center justify-center h-full">
        <p className="text-gray-600">Failed to load inventory</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-white flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Cloud Inventory</h2>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search clouds, accounts, regions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredInventory.clouds.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No items found</p>
        ) : (
          <div className="space-y-4">
            {filteredInventory.clouds.map((cloud, cloudIndex) => (
              <div key={cloudIndex} className="space-y-2">
                {/* Cloud */}
                <div className="flex items-center gap-2 text-gray-900 font-medium">
                  <Cloud className="w-5 h-5 text-gray-500" />
                  <span>{cloud.name}</span>
                </div>

                {/* Accounts */}
                <div className="ml-6 space-y-2">
                  {cloud.accounts.map((account) => (
                    <div key={account.id} className="space-y-1">
                      {/* Account */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleAccount(account.id)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          {expandedAccounts.has(account.id) ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                        <Building2 className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-900 font-medium">{account.display_name}</span>
                        <span className="text-gray-500 text-sm">({account.id})</span>
                      </div>

                      {/* Regions */}
                      {expandedAccounts.has(account.id) && (
                        <div className="ml-8 space-y-1">
                          {account.regions.map((region) => (
                            <div key={`${account.id}-${region.name}`} className="space-y-1">
                              {/* Region */}
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleRegion(`${account.id}-${region.name}`)}
                                  className="p-1 hover:bg-gray-100 rounded"
                                >
                                  {expandedRegions.has(`${account.id}-${region.name}`) ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                  )}
                                </button>
                                <MapPin className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-800">{region.name}</span>
                              </div>

                              {/* Reflectors and Clusters */}
                              {expandedRegions.has(`${account.id}-${region.name}`) && (
                                <div className="ml-8 space-y-2">
                                  {/* Reflectors */}
                                  {region.reflectors.map((reflector) => (
                                    <div
                                      key={reflector.id}
                                      onClick={() => handleEntitySelect({
                                        cloud: cloud.name,
                                        account_id: account.id,
                                        account_display_name: account.display_name,
                                        region: region.name,
                                        type: 'reflector',
                                        id: reflector.id,
                                        display_name: reflector.display_name
                                      })}
                                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                                        selectedEntity?.id === reflector.id ? 'bg-blue-50 border border-blue-200' : ''
                                      }`}
                                    >
                                      <Circle className={`w-3 h-3 ${getStatusColor(reflector.status)}`} />
                                      <Server className="w-4 h-4 text-gray-500" />
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">
                                          {reflector.display_name}
                                        </div>
                                        <div className="text-xs text-gray-500">{reflector.id}</div>
                                      </div>
                                      <div 
                                        className="relative"
                                        onMouseEnter={() => setHoveredItem(`reflector-${reflector.id}`)}
                                        onMouseLeave={() => setHoveredItem(null)}
                                      >
                                        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                        {hoveredItem === `reflector-${reflector.id}` && (
                                          <div className="absolute right-0 top-6 z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                                            <div className="space-y-1">
                                              <div>Status: {reflector.status}</div>
                                              {reflector.role && <div>Role: {reflector.role}</div>}
                                              {reflector.version && <div>Version: {reflector.version}</div>}
                                            </div>
                                            <div className="absolute right-4 -top-1 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}

                                  {/* Clusters */}
                                  {region.clusters.map((cluster) => (
                                    <div
                                      key={cluster.id}
                                      onClick={() => handleEntitySelect({
                                        cloud: cloud.name,
                                        account_id: account.id,
                                        account_display_name: account.display_name,
                                        region: region.name,
                                        type: 'cluster',
                                        id: cluster.id,
                                        display_name: cluster.display_name
                                      })}
                                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                                        selectedEntity?.id === cluster.id ? 'bg-blue-50 border border-blue-200' : ''
                                      }`}
                                    >
                                      <Circle className={`w-3 h-3 ${getStatusColor(cluster.status)}`} />
                                      <Users className="w-4 h-4 text-gray-500" />
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">
                                          {cluster.display_name}
                                        </div>
                                        <div className="text-xs text-gray-500">{cluster.id}</div>
                                      </div>
                                      <div 
                                        className="relative"
                                        onMouseEnter={() => setHoveredItem(`cluster-${cluster.id}`)}
                                        onMouseLeave={() => setHoveredItem(null)}
                                      >
                                        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                        {hoveredItem === `cluster-${cluster.id}` && (
                                          <div className="absolute right-0 top-6 z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                                            <div className="space-y-1">
                                              <div>Health: {cluster.health}</div>
                                              <div>Members: {cluster.reflectors.length}</div>
                                              <div>Status: {cluster.status || 'Unknown'}</div>
                                            </div>
                                            <div className="absolute right-4 -top-1 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}

                                  {/* Empty state */}
                                  {region.reflectors.length === 0 && region.clusters.length === 0 && (
                                    <p className="text-gray-500 text-sm py-2">No items</p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default CloudInventoryPanel;
